
import React from 'react';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext 
} from "@/components/ui/carousel";
import useEmblaCarousel from 'embla-carousel-react';
import AutoplayPlugin from 'embla-carousel-autoplay';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useIsMobile } from '@/hooks/use-mobile';

const WorkSamples = () => {
  const isMobile = useIsMobile();
  // Mock portfolio items - in real world, these would be actual images
  const portfolioItems = [
    {
      id: 1,
      imageUrl: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd',
      title: 'Gourmet Burger Post',
      category: 'Burgers',
    },
    {
      id: 2,
      imageUrl: 'https://images.unsplash.com/photo-1571091718767-18b5b1457add',
      title: 'Food Truck Event',
      category: 'Events',
    },
    {
      id: 3,
      imageUrl: 'https://images.unsplash.com/photo-1594212699903-ec8a3eca50f5',
      title: 'Weekly Special',
      category: 'Promos',
    },
    {
      id: 4,
      imageUrl: 'https://images.unsplash.com/photo-1610614819513-58e34989848b',
      title: 'Customer Testimonial',
      category: 'Testimonials',
    },
    {
      id: 5,
      imageUrl: 'https://images.unsplash.com/photo-1550547660-d9450f859349',
      title: 'Burger Promotion',
      category: 'Promos',
    },
    {
      id: 6,
      imageUrl: 'https://images.unsplash.com/photo-1551782450-a2132b4ba21d',
      title: 'Social Media Story',
      category: 'Social Media',
    },
  ];

  // Setup autoplay plugin for carousel with a slower speed
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, dragFree: true, containScroll: 'trimSnaps' }, 
    [AutoplayPlugin({ delay: 4000, stopOnInteraction: true })]
  );

  // Mobile view with Pinterest-style layout
  if (isMobile) {
    // Split items into two columns for Pinterest-style layout
    const leftColumnItems = portfolioItems.filter((_, index) => index % 2 === 0);
    const rightColumnItems = portfolioItems.filter((_, index) => index % 2 === 1);
    
    return (
      <section id="work" className="py-16 bg-white">
        <div className="section-container">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Work</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Imagine your business looking like this.
            </p>
            <div className="h-1 w-20 bg-grow99x-orange mx-auto mt-4"></div>
          </div>
          
          <div className="grid grid-cols-2 gap-2 mb-8">
            {/* Left column */}
            <div className="flex flex-col gap-2">
              {leftColumnItems.map((item) => (
                <Card key={item.id} className="overflow-hidden shadow-md">
                  <CardContent className="p-0 relative">
                    <img 
                      src={item.imageUrl} 
                      alt={item.title} 
                      className="w-full h-auto" 
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 touch-none active:opacity-100 flex flex-col justify-end p-2 text-white">
                      <h3 className="font-semibold text-sm">{item.title}</h3>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Right column */}
            <div className="flex flex-col gap-2">
              {rightColumnItems.map((item) => (
                <Card key={item.id} className="overflow-hidden shadow-md">
                  <CardContent className="p-0 relative">
                    <img 
                      src={item.imageUrl} 
                      alt={item.title} 
                      className="w-full h-auto" 
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 touch-none active:opacity-100 flex flex-col justify-end p-2 text-white">
                      <h3 className="font-semibold text-sm">{item.title}</h3>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Link to="/work">
              <Button variant="outline" className="hover:bg-grow99x-orange hover:text-white border-grow99x-orange text-grow99x-orange">
                View More Work
              </Button>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Desktop view with carousel
  return (
    <section id="work" className="py-16 bg-white">
      <div className="section-container">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Work</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Imagine your business looking like this.
          </p>
          <div className="h-1 w-20 bg-grow99x-orange mx-auto mt-4"></div>
        </div>
        
        <Carousel className="w-full mb-6">
          <CarouselContent ref={emblaRef}>
            {portfolioItems.map((item) => (
              <CarouselItem key={item.id} className="basis-full md:basis-1/2 lg:basis-1/3">
                <Card className="shadow-md group overflow-hidden m-2">
                  <CardContent className="p-0 relative">
                    <AspectRatio ratio={16/9}>
                      <img 
                        src={item.imageUrl} 
                        alt={item.title} 
                        className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 text-white">
                        <h3 className="font-semibold text-lg">{item.title}</h3>
                        <p className="text-sm">{item.category}</p>
                      </div>
                    </AspectRatio>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-2" />
          <CarouselNext className="right-2" />
        </Carousel>

        <div className="text-center mt-8">
          <Link to="/work">
            <Button variant="outline" className="hover:bg-grow99x-orange hover:text-white border-grow99x-orange text-grow99x-orange">
              View More Work
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WorkSamples;
